import React, { useState } from 'react';
import { Container } from './ui/Container';
import { Button } from './ui/Button';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
const images = [{
  src: 'https://dragonmartialarts-rl.com/wp-content/uploads/2023/10/IMG_0015-scaled.jpg',
  alt: 'Dragon Martial Arts of Round Lake — training session'
}, {
  src: 'https://dragonmartialarts-rl.com/wp-content/uploads/2023/10/IMG_0028.jpg',
  alt: 'Dragon Martial Arts of Round Lake — students in class'
}, {
  src: 'https://dragonmartialarts-rl.com/wp-content/uploads/2023/10/IMG_0044.jpg',
  alt: 'Dragon Martial Arts of Round Lake — Taekwondo practice'
}, {
  src: 'https://dragonmartialarts-rl.com/wp-content/uploads/2023/10/IMG_0063.jpeg',
  alt: 'Dragon Martial Arts of Round Lake — sparring'
}, {
  src: 'https://dragonmartialarts-rl.com/wp-content/uploads/2023/10/IMG_2667.jpg',
  alt: 'Dragon Martial Arts of Round Lake — demo team'
}, {
  src: 'https://dragonmartialarts-rl.com/wp-content/uploads/2023/10/IMG_2887-1.jpeg',
  alt: 'Dragon Martial Arts of Round Lake — community event'
}];
export function GallerySection() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const prev = () => {
    if (lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex - 1 + images.length) % images.length);
  };
  const next = () => {
    if (lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex + 1) % images.length);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') prev();
    if (e.key === 'ArrowRight') next();
    if (e.key === 'Escape') closeLightbox();
  };
  return <section className="py-20 bg-bg" data-id="element-125">
      <Container data-id="element-126">
        <div className="text-center mb-12" data-id="element-127">
          <h2 className="text-3xl md:text-4xl font-extrabold text-nav mb-4 uppercase tracking-tight font-heading" data-id="element-128">
            See Our Students In Action
          </h2>
          <div className="w-20 h-1 bg-accent mx-auto mb-6" data-id="element-129"></div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-130">
            Click any photo to view full size.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-12" data-id="element-131">
          {images.map((img, index) => <button key={index} onClick={() => openLightbox(index)} className="group relative aspect-[4/3] overflow-hidden rounded-sm shadow-md cursor-pointer block w-full focus:outline-none focus:ring-4 focus:ring-accent focus:ring-offset-2" aria-label={`View photo: ${img.alt}`} data-id="element-132">
              <img src={img.src} alt={img.alt} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-133" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center" data-id="element-134">
                <ZoomIn className="text-white opacity-0 group-hover:opacity-100 w-10 h-10 drop-shadow-lg transition-opacity duration-300" aria-hidden="true" data-id="element-135" />
              </div>
            </button>)}
        </div>

        {/* Section CTA */}
        <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-136">
          <a href="#signup" data-id="element-137">
            <Button variant="primary" size="lg" data-id="element-138">
              Get One Week Free Trial
            </Button>
          </a>
          <a href="tel:8472128045" data-id="element-139">
            <Button variant="secondary" size="lg" className="!text-nav !border-nav hover:!bg-nav hover:!text-white" data-id="element-140">
              Call (847) 212-8045
            </Button>
          </a>
        </div>
      </Container>

      {/* Lightbox Modal */}
      {lightboxIndex !== null && <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Image lightbox" onKeyDown={handleKeyDown} tabIndex={-1} data-id="element-141">
          {/* Close */}
          <button onClick={closeLightbox} className="absolute top-4 right-4 text-white hover:text-accent p-2 rounded-full bg-black/50 hover:bg-black/80 transition-colors focus:outline-none focus:ring-2 focus:ring-accent" aria-label="Close lightbox" data-id="element-142">
            <X className="w-7 h-7" aria-hidden="true" data-id="element-143" />
          </button>

          {/* Prev */}
          <button onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-accent p-3 rounded-full bg-black/50 hover:bg-black/80 transition-colors focus:outline-none focus:ring-2 focus:ring-accent" aria-label="Previous image" data-id="element-144">
            <ChevronLeft className="w-7 h-7" aria-hidden="true" data-id="element-145" />
          </button>

          {/* Image */}
          <div className="max-w-5xl max-h-[85vh] w-full flex items-center justify-center" data-id="element-146">
            <img src={images[lightboxIndex].src} alt={images[lightboxIndex].alt} className="max-w-full max-h-[85vh] object-contain rounded-sm shadow-2xl" data-id="element-147" />
          </div>

          {/* Next */}
          <button onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-accent p-3 rounded-full bg-black/50 hover:bg-black/80 transition-colors focus:outline-none focus:ring-2 focus:ring-accent" aria-label="Next image" data-id="element-148">
            <ChevronRight className="w-7 h-7" aria-hidden="true" data-id="element-149" />
          </button>

          {/* Counter */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-sm font-body" data-id="element-150">
            {lightboxIndex + 1} / {images.length}
          </div>
        </div>}
    </section>;
}